import { Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription } from 'rxjs';
import { MoneyService } from '../money.service';
import {
  FireSettings, MortgageSettings, DEFAULT_FIRE, DEFAULT_MORTGAGE, Txn,
  SWR_MIN_BP, SWR_MAX_BP, SWR_STEP_BP, SIM_MAX_MONTHS,
} from '../money.model';
import { formatCents } from '../format';
import { toCents, todayISO } from '../input';
import { LinePoint } from '../charts/line-chart.component';
import { targetDate, savingsRateChips, SavingsChip } from '../savings/savings.calc';
import {
  SpendBreakdown, SimResult, Comparison,
  perpetualAnnualSpend, compareScenarios, coastNumberCents, clampSwr, classifyComparison,
} from './fire.calc';

type Scenario = 'mortgage' | 'invest';

/** Annual resolution on the chart. A 721-point series on a 240-unit viewBox
 *  is illegible, and the figures beside it are annual too. */
const CHART_YEARS = 40;

@Component({
  selector: 'app-money-fire',
  templateUrl: './fire.component.html',
  styleUrls: ['./fire.component.scss'],
  standalone: false,
})
export class FireComponent implements OnInit, OnDestroy {
  loading = true;
  error = '';

  settings: FireSettings = { ...DEFAULT_FIRE };
  mortgage: MortgageSettings = { ...DEFAULT_MORTGAGE };
  txns: Txn[] = [];
  chips: SavingsChip[] = [];

  /** Dollar fields as typed; see the note in savings.component.ts on why
   *  these are strings and not a formatting getter/setter pair. */
  draft = { portfolio: '', contrib: '', manualSpend: '' };

  /**
   * Results, computed only when recalculate() is called — on first load, on
   * the Recalculate button, and when the scenario toggle changes which
   * scenario is plotted.
   *
   * Deliberately NOT getters. compareScenarios runs two 721-month
   * simulations, and a dozen template bindings read the result; behind a
   * getter Angular would re-run both simulations on every change-detection
   * pass, i.e. on every keystroke anywhere on the page.
   */
  spend: SpendBreakdown | null = null;
  comparison: Comparison | null = null;

  /** Inputs have changed since the last recalculate, so what is on screen is
   *  no longer what the form says. */
  stale = false;

  /** Which scenario the chart plots. The numeric head-to-head shows both
   *  regardless — four lines on one chart is illegible, two is not. */
  scenario: Scenario = 'mortgage';

  fireNumberCents = 0;
  coastCents: number | null = null;
  portfolioPoints: LinePoint[] = [];
  targetPoints: LinePoint[] = [];
  portfolioDisplay: string[] = [];
  targetDisplay: string[] = [];

  readonly fmt = formatCents;
  readonly swrMin = SWR_MIN_BP;
  readonly swrMax = SWR_MAX_BP;
  readonly swrStep = SWR_STEP_BP;

  private uid = '';
  private seeded = false;
  private txnsReady = false;
  private settingsReady = false;
  private subs: Subscription[] = [];
  private innerSubs: Subscription[] = [];

  constructor(private afAuth: AngularFireAuth, private money: MoneyService) {}

  ngOnInit(): void {
    this.subs.push(this.afAuth.authState.subscribe(user => {
      if (!user) return;
      this.uid = user.uid;
      this.seeded = false;
      this.txnsReady = false;
      this.settingsReady = false;
      // A re-login within this same instance (account switch, not a fresh
      // page load) must not leave the previous user's figures on screen
      // while the new streams are still arriving.
      this.loading = true;
      this.spend = null;
      this.comparison = null;
      this.coastCents = null;
      this.portfolioPoints = [];
      this.targetPoints = [];
      this.portfolioDisplay = [];
      this.targetDisplay = [];
      this.chips = [];
      this.stale = false;

      this.innerSubs.forEach(s => s.unsubscribe());
      this.innerSubs = [];

      this.innerSubs.push(this.money.subscribeAll(this.uid).subscribe({
        next: data => {
          this.txns = data.txns;
          this.chips = savingsRateChips(data.txns, todayISO());
          this.onData(() => { this.txnsReady = true; });
        },
        error: (e: unknown) => this.fail(e),
      }));

      this.innerSubs.push(this.money.subscribeFire(this.uid).subscribe({
        next: data => {
          this.settings = { ...data.fire };
          this.mortgage = { ...data.mortgage };
          // Seed the text fields once from storage; afterward they are the
          // user's own in-progress edit and must not be overwritten by a
          // background emission.
          if (!this.seeded) {
            this.draft = {
              portfolio: (data.fire.portfolioCents / 100).toFixed(2),
              contrib: (data.fire.monthlyContribCents / 100).toFixed(2),
              manualSpend: (data.fire.manualAnnualSpendCents / 100).toFixed(2),
            };
            this.seeded = true;
          }
          this.onData(() => { this.settingsReady = true; });
        },
        error: (e: unknown) => this.fail(e),
      }));
    }));
  }

  /**
   * Calculate once, automatically, when both streams have first delivered —
   * an empty screen on arrival would be worse than one stale click behind.
   *
   * Every emission after that marks the results stale instead of
   * recalculating: a mortgage edited on the other screen arrives here as a
   * background push, and silently re-running two simulations under the
   * user's cursor is exactly what the button exists to avoid.
   *
   * `loading` is cleared here too, not by either subscribe handler alone:
   * subscribeFire reads 2 RTDB paths and subscribeAll combines 10, so the
   * fire stream normally lands first. If the form unlocked as soon as it
   * did, an edit made in that window would markStale() before the first
   * calculation ever ran, and the emission that finally completes both
   * streams would silently recalculate over the user's half-typed draft —
   * exactly what the Recalculate button exists to prevent.
   */
  private onData(markReady: () => void): void {
    const wasReady = this.txnsReady && this.settingsReady;
    markReady();
    if (!this.txnsReady || !this.settingsReady) return;
    if (wasReady) { this.stale = true; return; }
    this.recalculate();
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.innerSubs.forEach(s => s.unsubscribe());
  }

  private fail(e: unknown): void {
    this.error = `Could not load your FIRE plan: ${(e as Error).message}. Try reloading the page.`;
    this.loading = false;
  }

  /**
   * `currentAge`/`coastAge` bounded and integral.
   *
   * [(ngModel)] writes the model on every keystroke but commit() clamps on
   * (change) — i.e. on blur. Bounding here, at the point the simulation
   * reads it, keeps a pasted or fractional value from reaching the
   * simulation before blur ever fires. Same reasoning as boundedTerm in
   * mortgage.component.ts.
   */
  get boundedCurrentAge(): number {
    const n = this.settings.currentAge;
    if (n === null || n === undefined || !Number.isFinite(n)) return DEFAULT_FIRE.currentAge;
    return Math.min(Math.max(Math.round(n), 0), 120);
  }

  get boundedCoastAge(): number {
    const n = this.settings.coastAge;
    if (n === null || n === undefined || !Number.isFinite(n)) return DEFAULT_FIRE.coastAge;
    return Math.min(Math.max(Math.round(n), 0), 120);
  }

  /** Bound at the point the simulation reads it, not only on commit:
   *  [(ngModel)] writes the model on every keystroke but (change) fires on
   *  blur, so a pasted value would otherwise reach the simulation first. */
  private get bounded(): FireSettings {
    return {
      ...this.settings,
      swrBasisPoints: clampSwr(this.settings.swrBasisPoints),
      currentAge: this.boundedCurrentAge,
      coastAge: this.boundedCoastAge,
    };
  }

  /**
   * `termMonths` bounded, mirroring `boundedTerm` in mortgage.component.ts: a
   * stored term above the simulation horizon makes `monthlyPayment` return
   * NaN, which `amortize`'s `available <= 0` guard does not catch (NaN
   * comparisons are false), producing NaN rows and `$NaN` on screen. This
   * screen only reads the mortgage, never writes it, so there is no commit()
   * to clamp on blur the way mortgage.component.ts does — bounding here, at
   * the point recalculate() reads it, is the only clamp this screen gets.
   */
  private get boundedMortgageTerm(): number {
    const n = Math.round(this.mortgage.termMonths);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, SIM_MAX_MONTHS);
  }

  private get boundedMortgage(): MortgageSettings {
    return { ...this.mortgage, termMonths: this.boundedMortgageTerm };
  }

  get hasResults(): boolean { return this.comparison !== null && this.spend !== null; }

  get active(): SimResult | null {
    if (!this.comparison) return null;
    return this.scenario === 'mortgage' ? this.comparison.toMortgage : this.comparison.toInvest;
  }

  get hasCoasted(): boolean {
    return this.coastCents !== null && this.settings.portfolioCents >= this.coastCents;
  }

  /**
   * True when the mortgage deduction alone exceeds the spending base it is
   * being subtracted from, so `perpetualAnnualSpend`'s zero floor has
   * kicked in and `totalCents` no longer equals base minus deduction.
   *
   * `ledgerCents === null` (nothing recorded in the window) is excluded:
   * that state already renders its own "no spending recorded" message above,
   * and a $0 perpetual figure reads correctly there without this note too.
   */
  get deductionCoversAllSpend(): boolean {
    if (!this.spend) return false;
    const base = this.settings.spendSource === 'manual'
      ? Math.max(this.settings.manualAnnualSpendCents, 0)
      : this.spend.ledgerCents;
    return base !== null && this.spend.mortgageDeductionCents > base;
  }

  fireDate(result: SimResult): string {
    return result.fireMonth === null ? '' : targetDate(todayISO(), result.fireMonth);
  }

  get swrPercent(): string {
    return (clampSwr(this.settings.swrBasisPoints) / 100).toFixed(2).replace(/\.?0+$/, '') + '%';
  }

  get ratePercent(): string {
    return (this.settings.rateBasisPoints / 100).toFixed(2).replace(/\.?0+$/, '') + '%';
  }

  /**
   * The head-to-head in one sentence. Rendered as prose rather than a bare
   * signed month count, which reads as noise.
   */
  get verdict(): string {
    const c = this.comparison;
    if (!c) return '';
    // Read the snapshot taken at run time, never the live mortgage: a
    // background push can zero the extra payment before these stale results
    // are recalculated, and testing live state here would make this branch say
    // "no extra set" directly above a head-to-head tile still built from a
    // run that had one — the exact tile-versus-prose split
    // extraMonthlyCentsAtRun was introduced to prevent.
    if (!(c.extraMonthlyCentsAtRun > 0)) {
      return 'Set an extra monthly payment on the Mortgage screen to compare.';
    }

    // Read the amount THIS comparison ran with, not the live mortgage — a
    // background push can change the latter before the stale results are
    // recalculated, which would otherwise pair a new dollar figure with an
    // old month gap.
    const extra = this.fmt(c.extraMonthlyCentsAtRun);
    const outcome = classifyComparison(c);

    if (outcome === 'neither') {
      return 'Neither plan reaches FI within 60 years at these numbers.';
    }
    if (outcome === 'mortgage-only') {
      return `Paying ${extra}/mo extra at the house reaches FI. Investing that ${extra}/mo `
        + 'instead does not reach FI within 60 years at these numbers.';
    }
    if (outcome === 'invest-only') {
      return `Investing that ${extra}/mo instead reaches FI. Paying it at the house `
        + 'does not reach FI within 60 years at these numbers.';
    }

    if (c.diffMonths === 0) return 'Both plans reach FI in the same month.';
    const months = Math.abs(c.diffMonths as number);
    const unit = months === 1 ? 'month' : 'months';
    return (c.diffMonths as number) > 0
      ? `Paying ${extra}/mo extra at the house reaches FI ${months} ${unit} sooner.`
      : `Investing that ${extra}/mo instead reaches FI ${months} ${unit} sooner.`;
  }

  /** Run the simulations and refresh every derived figure on the screen. */
  recalculate(): void {
    this.settings.portfolioCents = toCents(this.draft.portfolio);
    this.settings.monthlyContribCents = toCents(this.draft.contrib);
    this.settings.manualAnnualSpendCents = toCents(this.draft.manualSpend);

    const bounded = this.bounded;
    const boundedMortgage = this.boundedMortgage;
    this.spend = perpetualAnnualSpend(bounded, boundedMortgage, this.txns, todayISO());
    this.comparison = compareScenarios(bounded, boundedMortgage, this.spend.totalCents);
    this.coastCents = coastNumberCents(bounded, this.comparison.toMortgage.points);
    this.refreshChart();
    this.stale = false;
  }

  /** Annual samples of the plotted scenario, read off the stored simulation
   *  so the chart and the figures beside it can never disagree. */
  private refreshChart(): void {
    const result = this.active;
    if (!result) return;

    this.fireNumberCents = result.points[0].targetCents;

    const portfolio: LinePoint[] = [];
    const target: LinePoint[] = [];
    for (let year = 0; year <= CHART_YEARS; year++) {
      const p = result.points[year * 12];
      if (!p) break;
      portfolio.push({ label: `${year}y`, value: p.portfolioCents });
      target.push({ label: `${year}y`, value: p.targetCents });
    }

    this.portfolioPoints = portfolio;
    this.targetPoints = target;
    this.portfolioDisplay = portfolio.map(p => formatCents(p.value));
    this.targetDisplay = target.map(p => formatCents(p.value));
  }

  /** Switching which scenario is plotted reads the simulations already run —
   *  it does not need, and must not trigger, a recalculate. */
  setScenario(s: Scenario): void {
    this.scenario = s;
    this.refreshChart();
  }

  /** Any edit invalidates what is on screen until the button is pressed. */
  markStale(): void { this.stale = true; }

  useChip(chip: SavingsChip): void {
    this.draft.contrib = (chip.monthlyCents / 100).toFixed(2);
    this.commit();
  }

  /**
   * Persist the form. Called from (change), which fires on blur for text
   * inputs and on release for sliders — not per keystroke, which would be one
   * RTDB write per pixel of drag.
   *
   * Saving does NOT recalculate: the button owns that, so a long simulation
   * never runs under the user's cursor mid-edit.
   */
  async commit(): Promise<void> {
    this.settings.portfolioCents = toCents(this.draft.portfolio);
    this.settings.monthlyContribCents = toCents(this.draft.contrib);
    this.settings.manualAnnualSpendCents = toCents(this.draft.manualSpend);
    this.settings.swrBasisPoints = clampSwr(this.settings.swrBasisPoints);
    // Clamp the persisted values, not just the ones the simulation reads:
    // an out-of-range or cleared age must not reach Firebase as a float or
    // null, only to have withDefaults snap it back under the user's cursor.
    this.settings.currentAge = this.boundedCurrentAge;
    this.settings.coastAge = this.boundedCoastAge;
    this.stale = true;

    if (!this.uid) return;
    try { await this.money.saveFire(this.uid, this.settings); }
    catch (e) { this.fail(e); }
  }
}
